import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Star, Sparkles, DollarSign, Users, QrCode, Loader2, Instagram, Youtube, Music2,
  Copy, ExternalLink, Wand2, MessageSquare, Check, X,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import { Stat, StatusBadge, Pill } from "../components/shared";
import { useLang } from "../lib/i18n";

const tooltipStyle = { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, color: "#fff" };
const followerTrend = [
  { m: "Jan", f: 3200 }, { m: "Feb", f: 5100 }, { m: "Mar", f: 7400 },
  { m: "Apr", f: 9800 }, { m: "May", f: 13200 }, { m: "Jun", f: 18400 },
];

export default function ArtistDashboard() {
  const { t } = useLang();
  const d = t.dash.a;
  const [artists, setArtists] = useState([]);
  const [artist, setArtist] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tips, setTips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [score, setScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [genBio, setGenBio] = useState("");

  useEffect(() => {
    api.artists().then((a) => { setArtists(a); setArtist(a[0]); });
    api.venues().then(setVenues);
  }, []);

  const refresh = (aid) => {
    api.artistAnalytics(aid).then(setAnalytics);
    api.tips(aid).then(setTips);
    api.reviews(aid).then(setReviews);
    api.bookings({ artist_id: aid }).then(setBookings);
  };

  useEffect(() => {
    if (!artist) return;
    setScore(null); setGenBio("");
    refresh(artist.id);
  }, [artist]);

  const qrUrl = artist ? `${window.location.origin}/a/${artist.id}` : "";
  const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&color=D4AF37&bgcolor=0F0F0F&data=${encodeURIComponent(qrUrl)}`;

  const runScore = async () => {
    setScoreLoading(true);
    try { setScore(await api.aiScore(artist.id)); } catch { toast.error("Scoring failed"); }
    setScoreLoading(false);
  };
  const runBio = async () => {
    setBioLoading(true);
    try {
      const r = await api.aiBio({ name: artist.name, style: artist.style, languages: artist.languages.join(", ") });
      setGenBio(r.bio); toast.success("Demo biography ready");
    } catch { toast.error("Generation failed"); }
    setBioLoading(false);
  };
  const respond = async (id, status) => {
    await api.updateBooking(id, status);
    toast.success(`Booking ${status}`);
    refresh(artist.id);
  };

  if (!artist) return <div className="h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin gold" size={32} /></div>;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-2">{d.eyebrow}</p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl font-light">{d.welcome}, {artist.name.split(" ")[0]}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {artists.map((a) => (
            <Pill key={a.id} active={a.id === artist.id} onClick={() => setArtist(a)} testid={`artist-tab-${a.id}`}>{a.name}</Pill>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {/* Profile + QR */}
        <div className="card-lux overflow-hidden">
          <img src={artist.photo} alt={artist.name} className="w-full h-56 object-cover" />
          <div className="p-6">
            <h2 className="font-serif-lux text-2xl">{artist.name}</h2>
            <p className="text-sm text-neutral-500">{artist.style} · {artist.city}</p>
            <p className="text-xs text-neutral-400 mt-1">{artist.languages.join(" · ")}</p>
            <div className="flex gap-3 mt-4 text-neutral-400">
              <a href={artist.socials.instagram} target="_blank" rel="noreferrer" className="hover:text-white"><Instagram size={18} /></a>
              <a href={artist.socials.youtube} target="_blank" rel="noreferrer" className="hover:text-white"><Youtube size={18} /></a>
              <a href={artist.socials.spotify} target="_blank" rel="noreferrer" className="hover:text-white"><Music2 size={18} /></a>
            </div>
          </div>
        </div>

        {/* QR code */}
        <div className="card-lux p-6 flex flex-col items-center justify-center text-center">
          <p className="text-xs uppercase tracking-[0.3em] gold mb-4 flex items-center gap-2"><QrCode size={14} /> {d.personalQr}</p>
          <img src={qrImg} alt="QR code" className="rounded-sm border border-white/10" data-testid="artist-qr-code" />
          <p className="text-xs text-neutral-500 mt-4 break-all">{qrUrl}</p>
          <div className="flex gap-2 mt-4">
            <button data-testid="copy-qr-link" onClick={() => { navigator.clipboard.writeText(qrUrl); toast.success("Link copied"); }}
              className="btn-ghost-lux px-4 py-2 rounded-sm text-xs flex items-center gap-1"><Copy size={13} /> {d.copy}</button>
            <a data-testid="open-qr-page" href={qrUrl} target="_blank" rel="noreferrer"
              className="btn-gold px-4 py-2 rounded-sm text-xs flex items-center gap-1"><ExternalLink size={13} /> {d.preview}</a>
          </div>
        </div>

        {/* AI score */}
        <div className="card-lux p-6 gold-glow">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.3em] gold flex items-center gap-2"><Sparkles size={14} /> {d.aiScore}</p>
          </div>
          {!score ? (
            <div className="flex flex-col items-center justify-center h-40 gap-4">
              <p className="text-sm text-neutral-500 text-center">Get an AI-graded breakdown of your performance.</p>
              <button data-testid="ai-score-btn" onClick={runScore} disabled={scoreLoading} className="btn-gold px-5 py-2.5 rounded-sm text-sm font-semibold flex items-center gap-2">
                {scoreLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {d.analyseMe}
              </button>
            </div>
          ) : (
            <div data-testid="ai-score-result">
              <div className="flex items-end gap-3">
                <span className="font-serif-lux text-6xl gold">{score.score}</span>
                <span className="text-2xl text-neutral-500 mb-2">/ 100 · {score.grade}</span>
              </div>
              <p className="text-xs text-neutral-400 mt-2">{score.summary}</p>
              <div className="mt-3 space-y-1">
                {(score.strengths || []).map((s, i) => <p key={i} className="text-xs text-emerald-400">+ {s}</p>)}
                {(score.improvements || []).map((s, i) => <p key={i} className="text-xs text-amber-400">→ {s}</p>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat testid="artist-stat-earnings" label={d.totalEarnings} value={`£${analytics.earnings}`} sub="Fees + tips" />
          <Stat testid="artist-stat-tips" label={d.tipsReceived} value={`£${analytics.total_tips}`} sub={`${analytics.tip_count} tips`} />
          <Stat testid="artist-stat-followers" label={d.followersGained} value={`+${analytics.followers_gained}`} sub={`${analytics.engagement_rate}% engagement`} />
          <Stat testid="artist-stat-rating" label={d.rating} value={analytics.avg_rating || artist.rating} sub={`${analytics.review_count} reviews`} />
        </div>
      )}

      {/* Follower chart + AI bio */}
      <div className="grid lg:grid-cols-2 gap-4 mb-10">
        <div className="card-lux p-6">
          <p className="text-sm text-neutral-400 mb-4 flex items-center gap-2"><Users size={16} className="gold" /> {d.followerGrowth}</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={followerTrend}>
              <CartesianGrid strokeOpacity={0.1} vertical={false} />
              <XAxis dataKey="m" stroke="#666" fontSize={12} /><YAxis stroke="#666" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="f" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37" }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-lux p-6">
          <p className="text-sm text-neutral-400 mb-3 flex items-center gap-2"><Wand2 size={16} className="gold" /> {d.aiBio}</p>
          <p className="text-sm text-neutral-300 leading-relaxed min-h-[80px] border border-white/10 rounded-sm p-4 bg-black/20" data-testid="artist-bio-text">
            {genBio || artist.bio}
          </p>
          <button data-testid="ai-bio-btn" onClick={runBio} disabled={bioLoading} className="btn-gold px-5 py-2.5 rounded-sm text-sm font-semibold flex items-center gap-2 mt-4">
            {bioLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} {d.generateBio}
          </button>
        </div>
      </div>

      {/* Booking requests */}
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] gold mb-4">Booking Requests</p>
        <div className="card-lux divide-y divide-white/10" data-testid="artist-bookings-list">
          {bookings.length === 0 && <p className="p-6 text-sm text-neutral-500">No booking requests.</p>}
          {bookings.map((b) => {
            const v = venues.find((x) => x.id === b.venue_id);
            return (
              <div key={b.id} className="flex items-center justify-between p-5 gap-3">
                <div>
                  <p className="text-sm font-medium">{v?.name || b.venue_id} · {b.shift}</p>
                  <p className="text-xs text-neutral-500">{b.date} · £{b.fee}</p>
                </div>
                {b.status === "pending" ? (
                  <div className="flex gap-2">
                    <button data-testid={`accept-${b.id}`} onClick={() => respond(b.id, "confirmed")} className="btn-gold px-3 py-1.5 rounded-sm text-xs flex items-center gap-1"><Check size={13} /> {d.accept}</button>
                    <button data-testid={`decline-${b.id}`} onClick={() => respond(b.id, "declined")} className="btn-ghost-lux px-3 py-1.5 rounded-sm text-xs flex items-center gap-1"><X size={13} /> {d.decline}</button>
                  </div>
                ) : <StatusBadge status={b.status} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips + Reviews */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-4 flex items-center gap-2"><DollarSign size={14} /> Recent Tips</p>
          <div className="card-lux divide-y divide-white/10" data-testid="artist-tips-list">
            {tips.length === 0 && <p className="p-6 text-sm text-neutral-500">No tips yet.</p>}
            {tips.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4">
                <div><p className="text-sm">{t.guest_name}</p>{t.message && <p className="text-xs text-neutral-500">"{t.message}"</p>}</div>
                <span className="gold font-serif-lux text-xl">£{t.amount}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-4 flex items-center gap-2"><MessageSquare size={14} /> {d.reviews}</p>
          <div className="card-lux divide-y divide-white/10" data-testid="artist-reviews-list">
            {reviews.length === 0 && <p className="p-6 text-sm text-neutral-500">{d.noReviews}</p>}
            {reviews.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} className="gold fill-current" />)}
                  <span className="text-xs text-neutral-500">· {r.guest_name}</span>
                </div>
                <p className="text-sm text-neutral-300">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
